import { Input } from '@/components/Input/Input';
import { YStack } from 'tamagui';
import { FieldTitle } from '@components/FieldTitle/FieldTitle';

interface NumericFieldProps {
  /** Field label above the input */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Current value (as number or empty string) */
  value: number | null;
  /** Callback when value changes */
  onValueChange: (value: number | null) => void;
  /** Whether this field is required */
  required?: boolean;
  /** When true, the field becomes read-only (used for auto-calculated values) */
  disabled?: boolean;
}

/**
 * NumericField - Input field for numeric values (positive numbers only)
 *
 * Features:
 * - Only allows positive numbers
 * - Converts input to number type
 * - null for empty values
 */
export const NumericField = ({
  label,
  placeholder,
  value,
  onValueChange,
  required = false,
  disabled = false,
}: NumericFieldProps): React.ReactElement => {
  const handleTextChange = (text: string): void => {
    // Allow empty string (will be converted to null)
    if (text === '') {
      onValueChange(null);
      return;
    }

    // Only allow positive numbers
    const numericValue = parseFloat(text);
    if (!isNaN(numericValue) && numericValue >= 0) {
      onValueChange(numericValue);
    }
  };

  const displayValue = value !== null ? String(value) : '';

  return (
    <YStack gap="$1">
      {label && <FieldTitle label={label} required={required} />}
      <Input
        value={displayValue}
        onChangeText={handleTextChange}
        placeholder={placeholder}
        keyboardType="numeric"
        disabled={disabled}
        editable={!disabled}
      />
    </YStack>
  );
};
